import { useMemo } from "react";
import { Folder, Flows } from "../../../../../util/Types/Folder";
import { Space } from "../../../../../util/Types/Sidebar";
import { Namespace } from "../../../../../types/namespace";
import { FluxObject } from "../context";
import {
  organizeItemsByCategory,
  filterItems,
  createMainFolder,
  getAgentsFolderStructure,
  getSharedFolder,
  getPrivateFoldersToShow,
  getSpaceSharedFolderOrPlaceholder,
  getSharedAgentsFromFolder,
  type LocalOrganizedItems,
} from "../folderStructure";

export function useAgentsSidebarData(
  folders: Folder[],
  spaces: Space[],
  searchTerm: string,
  fluxObjects: FluxObject[],
  getSpaceObjects: (spaceId: string) => FluxObject[],
  namespaces: Namespace[],
) {
  const privateItemsRaw = useMemo(
    () =>
      organizeItemsByCategory(
        folders,
        [],
        fluxObjects,
        namespaces,
      ),
    [folders, fluxObjects, namespaces],
  );

  const allSpacesItemsRaw = useMemo(
    () =>
      spaces.map((space) =>
        organizeItemsByCategory(
          space.folder || [],
          space.flows || [],
          getSpaceObjects(space.id).length > 0
            ? getSpaceObjects(space.id)
            : space.objects || [],
          space.namespaces || [],
        ),
      ),
    [spaces, getSpaceObjects],
  );

  const hasSearchTerm = searchTerm.trim().length > 0;

  const allItems = useMemo(() => {
    if (!hasSearchTerm) return null;
    const combined: LocalOrganizedItems = {
      agents: [...privateItemsRaw.agents],
      fluxbots: [...privateItemsRaw.fluxbots],
      objects: [...privateItemsRaw.objects],
      vectorMemory: [...privateItemsRaw.vectorMemory],
    };
    allSpacesItemsRaw.forEach((spaceItems) => {
      combined.agents.push(...spaceItems.agents);
      combined.fluxbots.push(...spaceItems.fluxbots);
      combined.objects.push(...spaceItems.objects);
      combined.vectorMemory.push(...spaceItems.vectorMemory);
    });
    return filterItems(combined, searchTerm);
  }, [hasSearchTerm, searchTerm, privateItemsRaw, allSpacesItemsRaw]);

  const privateFoldersToShow = useMemo(
    () => getPrivateFoldersToShow(folders),
    [folders],
  );

  const searchResult = useMemo(() => {
    if (!hasSearchTerm) return null;
    const searchLower = searchTerm.toLowerCase();
    const agentsStructure = getAgentsFolderStructure(folders);
    const allSpacesAgentsStructure = spaces.map((space) =>
      getAgentsFolderStructure(space.folder || []),
    );
    const sharedFolder = getSharedFolder(folders);
    const sharedAgents = getSharedAgentsFromFolder(sharedFolder);
    const spacesSharedFolders = spaces.map((space) =>
      getSharedFolder(space.folder || []),
    );
    const spacesSharedAgents = spacesSharedFolders.flatMap((sf) =>
      getSharedAgentsFromFolder(sf),
    );
    const spacesSharedSubfolders = spacesSharedFolders.flatMap(
      (sf) => sf?.folders ?? [],
    );

    const allRootAgents: Flows[] = [
      ...agentsStructure.rootAgents,
      ...allSpacesAgentsStructure.flatMap((s) => s.rootAgents),
      ...sharedAgents,
      ...spacesSharedAgents,
    ];
    const allRootForms: Flows[] = [
      ...agentsStructure.rootForms,
      ...allSpacesAgentsStructure.flatMap((s) => s.rootForms),
    ];
    const allSubfolders: Folder[] = [
      ...agentsStructure.subfolders,
      ...allSpacesAgentsStructure.flatMap((s) => s.subfolders),
      ...(sharedFolder?.folders ?? []),
      ...spacesSharedSubfolders,
    ];

    const folderHasMatchingContent = (folder: Folder): boolean => {
      const nameMatch = folder.name?.toLowerCase().includes(searchLower);
      const flowMatch = folder.flows?.some((flow) =>
        flow.name?.toLowerCase().includes(searchLower),
      );
      const subfolderMatch = folder.folders?.some((sub) =>
        folderHasMatchingContent(sub),
      );
      return !!(nameMatch || flowMatch || subfolderMatch);
    };

    return {
      filteredSubfolders: allSubfolders.filter((sub) =>
        folderHasMatchingContent(sub),
      ),
      filteredRootAgents: allRootAgents.filter((agent) =>
        agent.name?.toLowerCase().includes(searchLower),
      ),
      filteredRootForms: allRootForms.filter((form) =>
        form.name?.toLowerCase().includes(searchLower),
      ),
    };
  }, [hasSearchTerm, searchTerm, folders, spaces]);

  function getSpaceSectionData(space: Space, spaceItems: LocalOrganizedItems) {
    const spaceFolders = space.folder || [];
    const agentsStructure = getAgentsFolderStructure(spaceFolders);
    const sharedFolderToShow = getSpaceSharedFolderOrPlaceholder(
      space.id,
      spaceFolders,
    );
    return {
      agentsStructure,
      sharedFolderToShow,
      spaceItems,
      vectorMemoryFolder: createMainFolder(
        "Vector Memory",
        spaceItems,
        space.id,
      ),
    };
  }

  return {
    privateItemsRaw,
    allSpacesItemsRaw,
    hasSearchTerm,
    allItems,
    privateFoldersToShow,
    searchResult,
    getSpaceSectionData,
  };
}
